import { useSelector } from "react-redux";
import { servicesArray, contactPayload } from "../common/JobsPayload";
import { CustomisedTitle, FlexRow, TextWrapper, TextWrapperSpan, Wrapper } from "../../../constants/common/StyledGlobalConstants";
import { Accordion, AccordionItem, AccordionHeader, AccordionDetail } from "@vds/accordions";
import styled from "styled-components";
import { Title } from "@vds/typography";
import CPNotification from "../../../pages/Login/CPNotification";
import CamerasAndAccessoriesDetails from "../RequestOverviewPage/CamerasAndAccessoriesDetails";
import VehicleDetails from "../RequestOverviewPage/VehicleDetails";
import { WARNING_REASON_DISPLAY_MESSAGES, SUPPORT_PHONE_NUMBERS } from "../../../constants/technicianAppointmentConstants";
import ContractAndCancellationsFAQWarning from "../common/ContractAndCancellationsFAQWarning";
import TechnicianPOC from "../RequestOverviewPage/TechnicianPOC";
import TechnicianAdditionalInstructions from "../RequestOverviewPage/TechnicianAdditionalInstructions";
import ServiceTotal from "../common/ServiceTotal";
import "../../../constants/common/StyleOnlyForScreenReader.css";
import "../TechnicianAppointmentsModal.css";
import { getPendoId } from "../../../util/cfstPendoUtils";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";

const { TABLET } = DEVICE_BREAKPOINTS;

const StyledAccordionItem = styled(AccordionItem)`
  #request_overview_accordion div[data-testid="date-test-id"] {
    padding-top: 0.2rem !important;
    padding-bottom: 0.2rem !important;
  }
`;
const StyledAccordionDetail = styled(AccordionDetail)`
  @media (max-width: 679px) {
    padding-bottom: 0px !important;
  }
`;

const RequestOverviewOnVinCompatibilityIssue = () => {
  const { serviceDetails, userSelectedContactOption, contactFormValues, notesToTechnician, isJobDurationExceeds10Hrs, isAssistanceRequired, serviceTotalFees } =
    useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const { windowSize, isSourcewellGovernment } = useSelector((state) => state.ACCOUNT);
  const isMobile = windowSize.width < TABLET;
  const technicianPOC = contactPayload(userSelectedContactOption, contactFormValues);

  
  // Helper functions to eliminate duplication
  const getSupportPhoneNumber = (isSourcewellGovernment) => {
    return isSourcewellGovernment ? SUPPORT_PHONE_NUMBERS.GOVERNMENT : SUPPORT_PHONE_NUMBERS.COMMERCIAL;
  };

  const getNotificationMessage = (isJobDurationExceeds10Hrs, isSourcewellGovernment) => {
    const phoneNumber = getSupportPhoneNumber(isSourcewellGovernment);

    if (isJobDurationExceeds10Hrs) {
      return (
        <>
          The services you requested will take the installer more than one day to complete. Booking this appointment will require agent support. Click{" "}
          <b>SUBMIT</b> to create a support case and we will contact you within two business days. Alternatively, you can call us on {phoneNumber}.
        </>
      );
    }

    return (
      <>
        One or more of your services require agent support to complete booking. Click <b>SUBMIT</b>, and we will create a support case and contact you
        within two business days. Alternatively, you can call us on {phoneNumber}.
      </>
    );
  };

  const allServiceDetails = [
    ...servicesArray(serviceDetails, true)
      .map((serviceData) =>
        serviceData.servicesDetails.map((service) => ({ ...service, serviceType: serviceData.type === "transfer" ? "Transfer" : "Removal" }))
      )
      .flat(),
  ];

  return (
      <Wrapper marginBottom="6rem" width="100%">
        <h1 className="sr-only" tabIndex="-1" role="alert">
          Request overview
        </h1>
        <Wrapper>
          <TextWrapper
            alignContent="center"
            fontSize="2.5rem"
            fontFamily="neue-haas-grotesk-display"
            fontWeight="700"
            lineHeight="3rem"
            aria-hidden="true"
            tabIndex="-1"
          >
            Request overview
          </TextWrapper>
        </Wrapper>
        <Wrapper marginTop="24px" role="alert">
          <CPNotification isOpen={true} notificationType="warning" closeButton={false} height="auto">
            <TextWrapperSpan fontWeight="400" fontSize="0.875rem" lineHeight="1.25rem">
              {getNotificationMessage(isJobDurationExceeds10Hrs, isSourcewellGovernment)}
            </TextWrapperSpan>
          </CPNotification>
        </Wrapper>
        <TextWrapper
          fontSize="0.875rem"
          lineHeight="1.25rem"
          fontWeight="400"
          marginTop="1rem !important"
          marginBottom="2rem"
          aria-label="Please ensure your details are correct."
        >
          {"Please ensure your details are correct."}
        </TextWrapper>
        <Accordion id={getPendoId("overview-accordion")} tabIndex={0}>
          <StyledAccordionItem tabIndex={0}>
            <AccordionHeader triggerType="icon" tabIndex={0} ariaLabel="Service required Accordion">
              <CustomisedTitle fontSize="24px" lineHeight="32px" style={{ wordBreak: "break-word", width: "95%", paddingLeft: "24px" }}>
                <Title size="large">Service required</Title>
              </CustomisedTitle>
            </AccordionHeader>
            <StyledAccordionDetail>
              <Wrapper>
                {allServiceDetails.map((service, index) => {
                  const { serviceType, relatedServices, existingVehicle, newVehicle, isWarning, warningReason } = service;
                  const {
                    id,
                    vin: oldVin,
                    vehicleName: oldVehicleName,
                    revealCoreESN: oldRevealCoreESN,
                    registrationNumber: oldRegistrationNumber,
                    cameras,
                  } = existingVehicle;
                  const newVehicleData = serviceType === "Transfer";
                  const isVin = !!oldVin;
                  return (
                    <Wrapper padding="0rem 1.5rem" marginTop={index > 0 && "2rem"} fontSize="0.875rem" key={id}>
                      <Wrapper border="1px solid #D8DADA" borderRadius="8px" padding="1.5rem">
                        <TextWrapper fontSize="1.25rem" fontWeight="700" lineHeight="1.5rem">
                          {serviceType}
                        </TextWrapper>
                        {isWarning && (
                          <Wrapper marginTop="1rem">
                            <CPNotification isOpen={true} notificationType="warning" closeButton={false} height="auto">
                              <TextWrapperSpan fontWeight="400" fontSize="0.875rem" lineHeight="1.25rem">
                                {WARNING_REASON_DISPLAY_MESSAGES[warningReason]}
                              </TextWrapperSpan>
                            </CPNotification>
                          </Wrapper>
                        )}
                        <FlexRow marginTop="1rem">
                          <VehicleDetails
                            vin={oldVin}
                            vehicleName={oldVehicleName}
                            esn={oldRevealCoreESN}
                            registrationNumber={oldRegistrationNumber}
                            isVin={isVin}
                          />
                          {!isMobile && isVin && <CamerasAndAccessoriesDetails camerasAndAccessories={relatedServices} cameras={cameras} />}
                        </FlexRow>
                        {newVehicleData ? (
                          newVehicle?.vehicleName && (
                            <Wrapper marginTop={newVehicleData && "1rem"}>
                              <VehicleDetails vin={newVehicle?.vin} vehicleName={newVehicle?.vehicleName} isVin={isVin} newVehicleData />
                              {isMobile && isVin && <CamerasAndAccessoriesDetails camerasAndAccessories={relatedServices} />}
                            </Wrapper>
                          )
                        ) : (
                          <Wrapper fontWeight="400" fontSize="0.875rem" flex="1" marginTop="1.5rem">
                            <ContractAndCancellationsFAQWarning />
                          </Wrapper>
                        )}
                      </Wrapper>
                    </Wrapper>
                  );
                })}
              </Wrapper>
            </StyledAccordionDetail>
          </StyledAccordionItem>
          <div></div>
        </Accordion>
        {(isAssistanceRequired || isJobDurationExceeds10Hrs) && <TechnicianPOC technicianPOC={technicianPOC} />}
        {isJobDurationExceeds10Hrs && (
          <>
            {notesToTechnician && (<TechnicianAdditionalInstructions TechnicianAdditionalInstructions={notesToTechnician} />)}
            <ServiceTotal fees={serviceTotalFees} />
            <TextWrapper marginTop={isMobile ? "2rem !important" : "2.5rem !important"} fontSize="0.875rem" fontWeight="400">
              You will be invoiced for the fees after the appointment has been completed.
            </TextWrapper>
          </>
        )}
        <TextWrapper
          fontSize="0.875rem"
          lineHeight="1.25rem"
          fontWeight="400"
          marginTop="1rem !important"
          aria-label=" By clicking <b>SUBMIT</b>, you confirm that the information provided is accurate."
        >
          By clicking <b>SUBMIT</b>, you confirm that the information provided is accurate.
        </TextWrapper>
      </Wrapper>
  );
};
export default RequestOverviewOnVinCompatibilityIssue;
